import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import "./EmployeeList.css";
import API_BASE_URL from "../config";

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [quickSort, setQuickSort] = useState("name");
  const [showFilters, setShowFilters] = useState(false);

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Download state
  const [downloading, setDownloading] = useState(false);

  const navigate = useNavigate();

  // 🔐 Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [navigate]);

  // Fetch employees
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/employees`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch employees");
        return res.json();
      })
      .then((data) => {
        setEmployees(data);
        setFilteredEmployees(data);
      })
      .catch((err) => setError(err.message));
  }, []);

  // Apply filters and sorting
  useEffect(() => {
    let result = [...employees];

    if (search) {
      result = result.filter(
        (emp) =>
          emp.firstName?.toLowerCase().includes(search.toLowerCase()) ||
          emp.lastName?.toLowerCase().includes(search.toLowerCase()) ||
          emp.email?.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (departmentFilter) {
      result = result.filter(
        (emp) =>
          emp.department &&
          emp.department.toLowerCase() === departmentFilter.toLowerCase()
      );
    }

    if (!showInactive) {
      result = result.filter((emp) => emp.user?.status !== "Inactive");
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const valA = getNestedValue(a, sortConfig.key);
        const valB = getNestedValue(b, sortConfig.key);

        if (valA == null) return 1;
        if (valB == null) return -1;

        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredEmployees(result);
  }, [search, departmentFilter, showInactive, employees, sortConfig]);

  const getNestedValue = (obj, key) => {
    const keys = key.split(".");
    return keys.reduce((acc, k) => (acc ? acc[k] : null), obj);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      } else {
        return { key, direction: "asc" };
      }
    });
  };

  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  // ------------ Report download -------------
  // This calls the backend endpoint you provided: /api/employees?format={pdf|xlsx}
  const downloadReport = async (format = "pdf") => {
    try {
      setDownloading(true);
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/reports/employees?format=${format}`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        const text = await res.text().catch(() => null);
        throw new Error(text || `Failed to download ${format} report`);
      }

      const blob = await res.blob();

      // Try to get filename from Content-Disposition header if present
      const contentDisposition = res.headers.get("Content-Disposition") || "";
      let filename = `Employee_Report.${format === "pdf" ? "pdf" : "xlsx"}`;
      const match = /filename="?([^";]+)"?/.exec(contentDisposition);
      if (match && match[1]) filename = match[1];

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="fixed-top">
        <AdminNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "80px" }}></div>
      <div className="d-flex flex-column min-vh-100">
        <div className="container-fluid p-3 bg-light flex-grow-1">
          {/* --- Page Header --- */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fw-bold">
              <i className="bi bi-people me-2"></i> Employees
            </h4>
            <div>
              <button
                className="btn btn-primary btn-sm me-2"
                onClick={() => navigate("/addemployee")}
              >
                New Employee
              </button>
            </div>
          </div>

          {/* --- Collapsible Filters --- */}
          <div
            className="p-2 bg-light border mb-2"
            style={{ cursor: "pointer" }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
          </div>
          {showFilters && (
            <div className="row mb-3">
              <div className="col-md-6 mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="col-md-4 mb-2">
                <select
                  className="form-select"
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <option value="">Filter by Department</option>
                  <option value="Deployment">Deployment</option>
                  <option value="Quality">Quality</option>
                  <option value="Production">Production</option>
                  <option value="Testing">Testing</option>
                </select>
              </div>
            </div>
          )}

          {/* --- Toolbar --- */}
          <div className="d-flex justify-content-between align-items-center p-2 bg-light border mb-2">
            <div className="d-flex align-items-center gap-2">
              {/* Report buttons: left = PDF, right = XLSX */}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => downloadReport("pdf")}
                disabled={downloading}
                title="Download PDF report"
              >
                {downloading ? "Downloading..." : "📄"}
              </button>

              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => downloadReport("xlsx")}
                disabled={downloading}
                title="Download Excel report"
              >
                {downloading ? "Downloading..." : "📊"}
              </button>

              <div className="form-check ms-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showInactive"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="showInactive">
                  Show Inactives
                </label>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span>Total: {filteredEmployees.length}</span>
            </div>
          </div>

          {/* --- Employee Table --- */}
          <div className="table-wrapper">
            <table className="table table-sm align-middle text-start">
              <thead>
                <tr>
                  <th>View | Edit</th>
                  <th>Photo</th>
                  <th onClick={() => handleSort("id")} style={{ cursor: "pointer" }}>
                    ID {getSortArrow("id")}
                  </th>
                  <th onClick={() => handleSort("user.status")} style={{ cursor: "pointer" }}>
                    Status {getSortArrow("user.status")}
                  </th>
                  <th onClick={() => handleSort("firstName")} style={{ cursor: "pointer" }}>
                    Name {getSortArrow("firstName")}
                  </th>
                  <th onClick={() => handleSort("phone")} style={{ cursor: "pointer" }}>
                    Phone {getSortArrow("phone")}
                  </th>
                  <th onClick={() => handleSort("email")} style={{ cursor: "pointer" }}>
                    Work Email {getSortArrow("email")}
                  </th>
                  <th onClick={() => handleSort("department")} style={{ cursor: "pointer" }}>
                    Department {getSortArrow("department")}
                  </th>
                  <th onClick={() => handleSort("designation")} style={{ cursor: "pointer" }}>
                    Designation {getSortArrow("designation")}
                  </th>
                  <th onClick={() => handleSort("dob")} style={{ cursor: "pointer" }}>
                    Birth Date {getSortArrow("dob")}
                  </th>
                  <th onClick={() => handleSort("doj")} style={{ cursor: "pointer" }}>
                    Joining Date {getSortArrow("doj")}
                  </th>
                  <th>Leaving Date</th>
                  <th>Marital Status</th>
                  <th>Anniversary Date</th>
                  <th>Gender</th>
                  <th>Degree</th>
                  <th>Institution</th>
                  <th>Field of Study</th>
                  <th>Edu Start</th>
                  <th>Edu End</th>
                  <th>Grade</th>
                  <th>Permanent Address</th>
                  <th>Current Address</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length > 0 ? (
                  filteredEmployees.map((emp) => (
                    <tr key={emp.id}>
                      <td>
                        <Link
                          to={`/viewemployee/${emp.id}`}
                          className="me-2 text-primary"
                        >
                          View
                        </Link>
                        |
                        <Link
                          to={`/editemployee/${emp.id}`}
                          className="ms-2 text-primary"
                        >
                          Edit
                        </Link>
                      </td>
                      <td>
                        {emp.photoPath ? (
                          <img
                            src={`${API_BASE_URL}/uploads/${emp.photoPath}`}
                            alt="Employee"
                            style={{
                              width: "35px",
                              height: "35px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td>{emp.id}</td>
                      <td>{emp.user?.status || ""}</td>
                      <td>
                        {emp.firstName} {emp.lastName}
                      </td>
                      <td>{emp.phone}</td>
                      <td>{emp.email}</td>
                      <td>{emp.department}</td>
                      <td>{emp.designation}</td>
                      <td>
                        {emp.dob
                          ? new Date(emp.dob).toLocaleDateString("en-GB")
                          : ""}
                      </td>
                      <td>
                        {emp.doj
                          ? new Date(emp.doj).toLocaleDateString("en-GB")
                          : ""}
                      </td>
                      <td>
                        {emp.dol
                          ? new Date(emp.dol).toLocaleDateString("en-GB")
                          : ""}
                      </td>
                      <td>{emp.mstatus}</td>
                      <td>
                        {emp.doa
                          ? new Date(emp.doa).toLocaleDateString("en-GB")
                          : ""}
                      </td>
                      <td>{emp.gender}</td>
                      <td>{emp.degree}</td>
                      <td>{emp.institution}</td>
                      <td>{emp.fieldOfStudy}</td>
                      <td>
                        {emp.eduStartDate
                          ? new Date(emp.eduStartDate).toLocaleDateString("en-GB")
                          : ""}
                      </td>
                      <td>
                        {emp.eduEndDate
                          ? new Date(emp.eduEndDate).toLocaleDateString("en-GB")
                          : ""}
                      </td>
                      <td>{emp.grade}</td>
                      <td>
                        {emp.street1}, {emp.street2}, {emp.city}, {emp.state}, " "
                        {emp.postalCode}, {emp.country}
                      </td>
                      <td>
                        {emp.currStreet1}, {emp.currStreet2}, {emp.currCity}, " "
                        {emp.currState}, {emp.currPostalCode}, " "
                        {emp.currCountry}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="25" className="text-muted">
                      No employees found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {error && <p className="text-danger mt-3">{error}</p>}
        </div>
      </div>
    </>
  );
};

export default EmployeeList;
